import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import AgencyLogo from '@components/agency/shared/AgencyLogo';
import {
  updateStoredEmployer,
  useScreeningAuth,
} from '@hooks/useScreeningAuth';
import { ShellTipsTrigger } from 'productTips';
import ScreeningQueryProvider from 'screening/queryClient';
import { isBillingReady } from 'screening/billing/catalog';
import { orgCopy } from 'screening/orgCopy';
import { usePrefetchNav } from 'screening/queries';
import { BillingRegion, ScreeningBilling } from 'screening/types';
import PlansGate from '../billing/PlansGate';
import ScreeningTips from '../tips/ScreeningTips';
import AccountMenu from './AccountMenu';

interface ScreeningAppShellProps {
  children: ReactNode;
  bleed?: boolean;
}

const MOBILE_BP = '640px';

type NavItem = {
  key: string;
  label: string;
  href: string;
  match: string[];
  exclude?: string[];
  demo?: boolean;
};

const navItems = (groupLabel: string): NavItem[] => [
  {
    key: 'jobs',
    label: 'Roles',
    href: '/screening',
    match: ['/screening', '/screening/jobs', '/screening/maps/roles'],
  },
  {
    key: 'groups',
    label: groupLabel,
    href: '/screening/teams',
    match: ['/screening/teams'],
  },
  {
    key: 'settings',
    label: 'Settings',
    href: '/screening/settings',
    match: ['/screening/settings'],
  },
  {
    key: 'map',
    label: 'Map',
    href: '/screening/maps',
    match: ['/screening/maps'],
    exclude: ['/screening/maps/people', '/screening/maps/roles'],
    demo: true,
  },
  {
    key: 'people',
    label: 'People',
    href: '/screening/maps/people',
    match: ['/screening/maps/people'],
    demo: true,
  },
];

const invitePath = (kind?: string | null) =>
  kind === 'provider' ? '/screening/maps/cohorts/new' : '/screening';

const InviteLink = styled.a`
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 12px;
  background: #032170;
  color: #ffffff;
  font-family: var(--screening-font-ui);
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    background: #021552;
    color: #ffffff;
  }
`;

const DrawerInvite = styled(InviteLink)`
  justify-content: center;
  margin-top: 16px;
`;

const Root = styled.div`
  min-height: 100vh;
  background: var(--screening-canvas);
  color: var(--screening-ink);
  font-family: var(--screening-font-ui);
`;

const TopBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 32px;
  background: var(--screening-paper);
  border-bottom: 1px solid var(--screening-hairline);

  @media (max-width: ${MOBILE_BP}) {
    padding: 12px 16px;
  }
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 28px;
  min-width: 0;

  @media (max-width: ${MOBILE_BP}) {
    gap: 12px;
  }
`;

const LogoLink = styled.a`
  display: inline-flex;
  align-items: center;
`;

const DesktopNav = styled.nav`
  display: flex;
  gap: 4px;

  @media (max-width: ${MOBILE_BP}) {
    display: none;
  }
`;

const NavLink = styled.a<{ $active: boolean }>`
  font-family: var(--screening-font-ui);
  font-size: 14px;
  font-weight: 600;
  color: ${({ $active }) =>
    $active ? 'var(--screening-ink)' : 'var(--screening-muted)'};
  text-decoration: none;
  white-space: nowrap;
  padding: 6px 12px;
  border-radius: var(--screening-r-input);
  background: ${({ $active }) =>
    $active ? 'var(--screening-canvas)' : 'transparent'};

  &:hover {
    color: var(--screening-ink);
  }
`;

const DrawerNavLink = styled(NavLink)`
  display: flex;
  width: 100%;
  padding: 10px 12px;
`;

const DemoTag = styled.span`
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  background: var(--screening-hold-wash);
  color: #7a4b00;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  vertical-align: 1px;
`;

const DesktopAccount = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: ${MOBILE_BP}) {
    display: none;
  }
`;

const MobileActions = styled.div`
  display: none;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: ${MOBILE_BP}) {
    display: flex;
  }
`;

const MenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid var(--screening-hairline);
  border-radius: var(--screening-r-button);
  background: var(--screening-paper);
  color: var(--screening-ink);
  cursor: pointer;
  padding: 0;

  &:hover {
    background: var(--screening-canvas);
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(16, 21, 28, 0.36);
  z-index: 50;
`;

const Drawer = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(320px, 100vw);
  background: var(--screening-paper);
  border-left: 1px solid var(--screening-hairline);
  box-shadow: var(--screening-shadow-overlay);
  z-index: 60;
  display: flex;
  flex-direction: column;
  padding: 16px;
  overflow-y: auto;
`;

const DrawerHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
`;

const DrawerTitle = styled.p`
  margin: 0;
  font-family: var(--screening-font-display);
  font-size: 15px;
  font-weight: 700;
  color: var(--screening-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const DrawerNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Main = styled.main`
  width: 100%;
`;

const MainBox = styled.div<{ $wide?: boolean }>`
  max-width: ${(p) => (p.$wide ? '1240px' : '1120px')};
  margin: 0 auto;
  padding: 40px 32px 96px;

  @media (max-width: 640px) {
    padding: 24px 16px 64px;
  }
`;

const MenuIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M4 7h16M4 12h16M4 17h16"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M6 6l12 12M18 6 6 18"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
    />
  </svg>
);

const underPath = (path: string, match: string) =>
  path === match || path.startsWith(`${match}/`);

export const isNavActive = (
  matches: string[],
  path: string,
  exclude: string[] = []
) =>
  !exclude.some((skip) => underPath(path, skip)) &&
  matches.some((match) =>
    match === '/screening'
      ? path === '/screening' || path.startsWith('/screening/jobs')
      : underPath(path, match)
  );

const isActive = (item: NavItem, path: string) =>
  isNavActive(item.match, path, item.exclude);

const ShellInner = ({ children, bleed = false }: ScreeningAppShellProps) => {
  const router = useRouter();
  const [menuOpen, setMenuOpen] = useState(false);
  const { isReady, isAuthenticated, employer, user, signOut } =
    useScreeningAuth();
  const [billing, setBilling] = useState<ScreeningBilling | null | undefined>(
    () => employer?.billing
  );
  const [billingRegion, setBillingRegion] = useState<
    BillingRegion | null | undefined
  >(() => employer?.billing_region);

  useEffect(() => {
    if (!employer) return;
    setBilling(employer.billing);
    setBillingRegion(employer.billing_region);
  }, [employer]);

  const onBillingReady = useCallback(
    (
      next: ScreeningBilling,
      extras?: Partial<{
        country_code: string | null;
        billing_region: BillingRegion | null;
      }>
    ) => {
      setBilling(next);
      if (extras?.billing_region !== undefined) {
        setBillingRegion(extras.billing_region);
      }
      if (employer) {
        updateStoredEmployer({
          ...employer,
          billing: next,
          country_code:
            extras?.country_code !== undefined
              ? extras.country_code
              : employer.country_code,
          billing_region:
            extras?.billing_region !== undefined
              ? extras.billing_region
              : employer.billing_region,
        });
      }
    },
    [employer]
  );

  useEffect(() => {
    const onRoute = () => setMenuOpen(false);
    router.events.on('routeChangeStart', onRoute);
    return () => router.events.off('routeChangeStart', onRoute);
  }, [router.events]);

  useEffect(() => {
    if (!isReady || !employer || !user) return;
    if (user.role === 'team_lead' || !employer.needs_setup) return;
    if (router.pathname.startsWith('/screening/onboarding')) return;
    router.replace('/screening/onboarding');
  }, [isReady, employer, user, router]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const items = navItems(orgCopy(employer?.org_kind).nav);
  const prefetch = usePrefetchNav();
  const prefetchRef = React.useRef(prefetch);
  prefetchRef.current = prefetch;
  useEffect(() => {
    if (!employer) return;
    const list = navItems(orgCopy(employer.org_kind).nav);
    list.forEach((item) => {
      prefetchRef.current(item.key);
      router.prefetch(item.href).catch(() => undefined);
    });
  }, [employer, router]);
  const holdForSetup =
    Boolean(employer?.needs_setup) &&
    user?.role !== 'team_lead' &&
    !router.pathname.startsWith('/screening/onboarding');

  if (!isReady || !isAuthenticated || !employer || !user || holdForSetup) {
    return <Root className="screening-root" />;
  }

  const billingNow = billing || employer.billing;
  const trialReady =
    employer.org_kind === 'provider' ||
    isBillingReady(billingNow?.status, billingNow?.plan_id);
  const showPlansGate = !trialReady;

  return (
    <ScreeningTips billingReady={trialReady}>
      {showPlansGate && (
        <PlansGate
          employerId={employer.id}
          billingRegion={billingRegion}
          billing={billing}
          onBillingReady={onBillingReady}
        />
      )}
      <Root className="screening-root" data-tour="shell-root">
        <TopBar data-tour="shell-topbar">
          <Brand>
            <Link href="/screening" passHref legacyBehavior>
              <LogoLink aria-label="HireMe" data-tour="shell-logo">
                <AgencyLogo width={26} height={28} />
              </LogoLink>
            </Link>
            <DesktopNav data-tour="shell-nav">
              {items.map((item) => (
                <Link key={item.href} href={item.href} passHref legacyBehavior>
                  <NavLink
                    $active={isActive(item, router.pathname)}
                    aria-current={
                      isActive(item, router.pathname) ? 'page' : undefined
                    }
                    data-tour={item.key === 'jobs' ? 'nav-jobs' : undefined}
                    data-nav-key={item.key}
                    onMouseEnter={() => prefetch(item.key)}
                    onFocus={() => prefetch(item.key)}
                  >
                    {item.label}
                    {item.demo && <DemoTag>Demo</DemoTag>}
                  </NavLink>
                </Link>
              ))}
            </DesktopNav>
          </Brand>
          <DesktopAccount data-tour="shell-account">
            <ShellTipsTrigger />
            <Link href={invitePath(employer.org_kind)} passHref legacyBehavior>
              <InviteLink>Invite people</InviteLink>
            </Link>
            <AccountMenu employer={employer} user={user} onSignOut={signOut} />
          </DesktopAccount>
          <MobileActions>
            <ShellTipsTrigger compact />
            <AccountMenu
              employer={employer}
              user={user}
              onSignOut={signOut}
              compact
            />
            <MenuButton
              type="button"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="screening-mobile-drawer"
              onClick={() => setMenuOpen((v) => !v)}
            >
              {menuOpen ? <CloseIcon /> : <MenuIcon />}
            </MenuButton>
          </MobileActions>
        </TopBar>

        {menuOpen && (
          <>
            <Backdrop onClick={() => setMenuOpen(false)} aria-hidden />
            <Drawer
              id="screening-mobile-drawer"
              role="dialog"
              aria-modal="true"
            >
              <DrawerHead>
                <DrawerTitle>{employer.company_name}</DrawerTitle>
                <MenuButton
                  type="button"
                  aria-label="Close menu"
                  onClick={() => setMenuOpen(false)}
                >
                  <CloseIcon />
                </MenuButton>
              </DrawerHead>
              <DrawerNav>
                {items.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    passHref
                    legacyBehavior
                  >
                    <DrawerNavLink $active={isActive(item, router.pathname)}>
                      {item.label}
                      {item.demo && <DemoTag>Demo</DemoTag>}
                    </DrawerNavLink>
                  </Link>
                ))}
              </DrawerNav>
              <Link
                href={invitePath(employer.org_kind)}
                passHref
                legacyBehavior
              >
                <DrawerInvite>Invite people</DrawerInvite>
              </Link>
            </Drawer>
          </>
        )}

        <Main>
          {bleed ? (
            children
          ) : (
            <MainBox $wide={router.pathname.startsWith('/screening/maps')}>
              {children}
            </MainBox>
          )}
        </Main>
      </Root>
    </ScreeningTips>
  );
};

const ScreeningAppShell = ({ children, bleed }: ScreeningAppShellProps) => (
  <ScreeningQueryProvider>
    <ShellInner bleed={bleed}>{children}</ShellInner>
  </ScreeningQueryProvider>
);

export default ScreeningAppShell;
